import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  FormControl,
  Input,
  SimpleGrid,
  Text,
  useMediaQuery
} from "@chakra-ui/react";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Context, useCurrentProfile } from "../../ContextWrapper";
import NotificationContainer from "../generic/updateNotification";

export const MyDetails = ({
  setNavIndex = null,
  setNavVisible = null,
  trigger,
}) => {
  const [isMobile] = useMediaQuery("(max-width: 1280px)");
  const [profileData, setProfileData] = useState({});
  const [initialData, setInitialData] = useState({});
  const [toggleDisabled, setToggleDisabled] = useState(false);
  const context = React.useContext(Context);
  const { profile } = useCurrentProfile();

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    const refreshProfileData = async (id) => {
      if (id) {
        try {
          const res = await axios.get(`/api/v2/profiles/${id}/`, {
            headers: { "Content-Type": "application/json" },
            withCredentials: true,
            signal: controller.signal,
          });
          if (isActive) {
            setProfileData(res.data);
            setInitialData(res.data);
          }
        } catch (err) {
          if (err?.response) {
            console.log(err);
          }
        }
      }
    };

    refreshProfileData(profile.id);

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [profile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
  };

  const checkMatch = (e) => {
    const firstName =
      profileData?.first_name !== undefined &&
      profileData?.first_name !== initialData?.user?.first_name;
    const lastName =
      profileData?.last_name !== undefined &&
      profileData?.last_name !== initialData?.user?.last_name;
    const phoneNumber = profileData?.phone_number !== initialData.phone_number;

    if (firstName || lastName || phoneNumber) {
      return true;
    } else {
      return false;
    }
  };

  const handleCancel = (e) => {
    setProfileData(initialData);
  };

  const handleSubmit = async (event, profileFormData) => {
    event.preventDefault();
    setToggleDisabled(true);

    let url = `/api/v2/profiles/${profile.id}/`;

    const formData = new FormData();
    if (profileFormData?.first_name) {
      formData.append("first_name", profileFormData.first_name);
    }
    if (profileFormData?.last_name) {
      formData.append("last_name", profileFormData.last_name);
    }
    if (profileFormData?.phone_number) {
      const phoneRegex = /^\+?\d{1,3}\d{9,15}$/;
      if (!phoneRegex.test(profileFormData.phone_number)) {
        // Show an error message if the phone number is invalid
        context.fireToast(
          "Error",
          "Please enter a valid phone number.",
          "error"
        );
        setToggleDisabled(false);
        return;
      }
      formData.append("phone_number", profileFormData.phone_number);
    }
    if (profileFormData?.profile_picture) {
      formData.append("profile_picture", profileFormData.profile_picture);
    }
    try {
      let res;
      res = await axios.patch(url, formData);

      if (res.status < 300) {
        setProfileData({
          ...profileData,
          ...res.data,
        });
        setInitialData({
          ...profileData,
          ...res.data,
        });
        trigger(true);
        context.fireToast(
          "Success",
          "Your profile has been successfully updated!",
          "success"
        );
        setToggleDisabled(false);
      } else {
        context.fireToast("Error", "Failed to fetch data from server", "error");
        setToggleDisabled(false);
      }
    } catch (err) {
      if (err?.response) {
        console.log(err);
      }
      setToggleDisabled(false);
    }
  };

  return (
    <Box pt={!isMobile ? "100px" : "150px"} pl={"24px"} pr={"24px"} pb={"24px"}>
      <NotificationContainer rounded={true} p={"0px 0px 24px"} />
      <form id="profileForm" onSubmit={(e) => handleSubmit(e, profileData)}>
        <Card borderRadius={"12px"}>
          <CardHeader p={0}>
            <Text
              p={"20px 20px 20px 20px"}
              fontSize={"22px"}
              fontWeight={600}
              lineHeight={"30px"}
            >
              Profile
            </Text>

            <Box borderBottom={"1px"} borderColor={"#DEE2E6"}></Box>
          </CardHeader>
          <CardBody height={"700px"} pt={"32px"}>
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              gridGap={{ base: "20px", md: "40px" }}
              pb={"20px"}
            >
              <Box>
                <Text
                  pb={"7px"}
                  fontSize={"15px"}
                  fontWeight={600}
                  lineHeight={"21px"}
                >
                  First Name
                </Text>
                <FormControl>
                  <Input
                    h={"41px"}
                    borderRadius={"8px"}
                    type={"text"}
                    name="first_name"
                    onChange={handleInputChange}
                    value={
                      profileData?.first_name ??
                      profileData?.user?.first_name ??
                      ""
                    }
                    placeholder="Enter your first name"
                  />
                </FormControl>
              </Box>
              <Box>
                <Text
                  pb={"7px"}
                  fontSize={"15px"}
                  fontWeight={600}
                  lineHeight={"21px"}
                >
                  Last Name
                </Text>
                <FormControl>
                  <Input
                    h={"41px"}
                    borderRadius={"8px"}
                    type={"text"}
                    name="last_name"
                    onChange={handleInputChange}
                    value={
                      profileData?.last_name ??
                      profileData?.user?.last_name ??
                      ""
                    }
                    placeholder="Enter your last name"
                  />
                </FormControl>
              </Box>
            </SimpleGrid>
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              gridGap={{ base: "20px", md: "40px" }}
            >
              <Box>
                <Text
                  pb={"7px"}
                  fontSize={"15px"}
                  fontWeight={600}
                  lineHeight={"21px"}
                >
                  Email
                </Text>
                <FormControl>
                  <Input
                    h={"41px"}
                    borderRadius={"8px"}
                    readOnly={true}
                    type={"email"}
                    value={
                      profileData?.user?.email
                        ? profileData.user.email
                        : ""
                    }
                  />
                </FormControl>
              </Box>
              <Box>
                <Text
                  pb={"7px"}
                  fontSize={"15px"}
                  fontWeight={600}
                  lineHeight={"21px"}
                >
                  Phone Number
                </Text>
                <FormControl>
                  <Input
                    h={"41px"}
                    borderRadius={"8px"}
                    type="text"
                    name="phone_number"
                    onChange={handleInputChange}
                    value={
                      profileData?.phone_number ? profileData.phone_number : ""
                    }
                    placeholder="Enter your phone number"
                  />
                </FormControl>
              </Box>
            </SimpleGrid>
          </CardBody>
          <CardFooter pt={"4px"} pb={"32px"}>
            <Box display={"block"} width={"100%"}>
              <Box>
                {checkMatch() && (
                  <Box
                    width={"100%"}
                    textAlign={"right"}
                    pt={"24px"}
                    display="flex"
                    justifyContent="flex-end"
                    gap="16px" // Add space between the buttons
                  >
                    <Button
                      type="submit"
                      isDisabled={toggleDisabled}
                      fontSize={"16px"}
                      fontWeight={"500"}
                      height={"36px"}
                      width={"90px"}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outline"
                      isDisabled={toggleDisabled}
                      fontSize={"16px"}
                      fontWeight={"500"}
                      height={"36px"}
                      width={"90px"}
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </CardFooter>
        </Card>
      </form>
    </Box>
  );
};
